import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "./components/Logo";
import IconButton from "./components/IconButton";
import MobileMenuToggle from "./components/MobileMenuToggle";
import MobileMenu from "./components/MobileMenu";
import type { HeaderProps } from "./Header.types";
import { Button } from "@/design-system/Atoms";
import { useScrollDirection } from "@/hooks/useScrollDirection";
import { useAuthService } from "@/rtkQuery/features/hooks";
import { ROUTES } from "@/routes/routes.config";
import { buildDashboardTabUrl } from "@/hooks/useBuildWithParams";
import CartSlider from "@/design-system/Molecules/CartSlider";
import { useCartDrawerPanel } from "@/hooks/useCartDrawerPanel";
import { DASHBOARD_TABS } from "@/constants/dashboardTabs";
import { usePatientCTAButtonConfig } from "@/hooks/usePatientCTAButtonConfig";
import { useCartService } from "@/rtkQuery/features/hooks/useCartService";

const Header: React.FC<HeaderProps> = ({
  logo,
  navItems,
  icons,
  ctaButton,
  mobileMenuLinks = [],
}) => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuthService();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLogoHidden, setIsLogoHidden] = useState(false);
  const scrollDirection = useScrollDirection();
  const { ctaButtonConfig, patientEntitlementsIsLoading } =
    usePatientCTAButtonConfig("Get Started");

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setIsLogoHidden(false);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsLogoHidden(false);
  };
  const { toggleCart: dispatchToggleCart } = useCartDrawerPanel();
  const { cartDataLength = undefined , cartIsLoading = true,} = useCartService() || {};


  useEffect(() => {
    if (!isMobileMenuOpen || window.innerWidth >= 992) {
      setIsLogoHidden(false);
      return;
    }

    let lastScrollY = window.scrollY;
    const threshold = 10;

    const handleMobileMenuScroll = () => {
      const currentScrollY = window.scrollY;

      if (Math.abs(currentScrollY - lastScrollY) < threshold) return;

      if (currentScrollY > lastScrollY) {
        setIsLogoHidden(true);
      } else {
        setIsLogoHidden(false);
      }

      lastScrollY = currentScrollY;
    };

    window.addEventListener("scroll", handleMobileMenuScroll, {
      passive: true,
    });
    return () => window.removeEventListener("scroll", handleMobileMenuScroll);
  }, [isMobileMenuOpen]);

  const getHeaderTransform = () => {
    const isMobile = window.innerWidth < 992;

    if (isMobile && isMobileMenuOpen) {
      return isLogoHidden ? "-translate-y-16" : "translate-y-0";
    }

    return scrollDirection === "down" ? "-translate-y-full" : "translate-y-0";
  };

  const getVisibleIcons = () =>
    icons
      .filter((icon) => {
        if (icon.id === "login") return !isAuthenticated;
        if (icon.id === "logout") return isAuthenticated;
        if (icon.id === "user") return isAuthenticated;
        if (icon.id === "cart") return isAuthenticated;
        return true;
      })
      .map((icon) => {
        if (icon.id === "logout") {
          return {
            ...icon,
            onClick: async () => {
              await logout();
              navigate(ROUTES.LOGIN);
            },
          };
        }
        if (icon.id === "login") {
          return {
            ...icon,
            onClick: async () => {
              navigate(ROUTES.LOGIN);
            },
          };
        }
        if (icon.id === "user") {
          return {
            ...icon,
            onClick: async () => {
              navigate(buildDashboardTabUrl(DASHBOARD_TABS.UPDATE));
            },
          };
        }
        if (icon.id === "cart") {
          return {
            ...icon,
            badgeCount: cartDataLength,
            isBadgeCountLoading: cartIsLoading,
            onClick: () => {
              dispatchToggleCart();
            },
          };
        }
        return icon;
      });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
        setIsLogoHidden(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobileMenuOpen]);

  return (
    <header
      className={`bg-primary fixed w-full z-50 transition-transform duration-300 ${getHeaderTransform()}`}
      style={{ top: "0px", paddingBlock: "1px" }}
    >
      <div className="header-container">
        <nav className="navbar navbar-expand-lg navbar-dark">
          <div className="h-16 flex items-center">
            <Logo {...logo} />
          </div>

          <div className="lg:ms-auto">
            <ul className="hidden mdlg:flex pl-0 mb-0 list-none transition-all duration-300 gap-6">
              {navItems.map((item) => (
                <li key={item.id}>
                  <Link
                    to={item.to}
                    onClick={isMobileMenuOpen ? closeMobileMenu : undefined}
                    className="underline-animate text-white px-1 py-2"
                    aria-current={item.active ? "page" : undefined}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center ms-7">
            {getVisibleIcons().map((iconConfig, index, arr) => (
              <IconButton
                key={iconConfig.id}
                config={iconConfig}
                className={
                  index === arr.length - 1 ? "me-1 lg:me-4" : "me-1 lg:me-2"
                }
              />
            ))}

            <MobileMenuToggle
              isOpen={isMobileMenuOpen}
              onToggle={toggleMobileMenu}
              isDark={false}
            />
            <Button
              variant="secondary"
              size="xl"
              className={`hide-below-991 md:flex lg:flex btn ${ctaButton.className || "btn-secondary"}`}
              onClick={ctaButtonConfig.action}
              loading={patientEntitlementsIsLoading}
            >
              {ctaButtonConfig.text}
            </Button>
          </div>
        </nav>

        <MobileMenu
          isOpen={isMobileMenuOpen}
          navItems={navItems}
          ctaButton={ctaButton}
          mobileMenuLinks={mobileMenuLinks}
          onClose={closeMobileMenu}
        />
      </div>
      <CartSlider />
    </header>
  );
};

export default Header;
